/**
 * Data tab (screenshots/76; §2.12): source dataset, fields dragged onto Bars / Value / Split by / Tooltip, filters,
 * "Follow the viewer", refresh. Picking up a field (drag, or Enter) lights up every slot that takes it and dims the rest
 * with the reason (`acceptsField`); Enter on a slot drops it (81 #4). The query results show under the preview.
 */
import { useRef, useState } from "react";
import { CIcon } from "../icons";
import { Dropdown } from "../../../components/shared/Dropdown";
import { DATASETS, acceptsField, datasetsFor, type ChartSettings, type DatasetId, type Field, type Filter, type Group, type Slot } from "../chartSettings";
import { useChartSettings, type ChartSettingsStore } from "../chartSettingsStore";
import { Switch } from "../../../components/shared/ui";
import { Row, Section, Seg, prettyValue } from "./ui";

export const FIELD_ICON: Record<Field["type"], string> = { text: "text", number: "hash", currency: "dollar-sign", date: "calendar", category: "tag", ward: "hash", person: "user-round", org: "building-2" };
const SLOT_LABEL: Record<Slot, string> = { category: "Bars", value: "Value", split: "Split by", tooltip: "Tooltip" };
const OP_LABEL: Record<Filter["op"], string> = { is: "is", isNot: "is not", in: "in", gte: "≥", lte: "≤" };

export interface DataTabProps { store: ChartSettingsStore; group: Group; problems: string[]; held: Field | null; setHeld: (f: Field | null) => void }

export function DataTab({ store, group, problems, held, setHeld }: DataTabProps) {
  const s = useChartSettings(store, (x) => x.settings);
  const ds = DATASETS[s.data.datasetId];
  const others = useRef<HTMLDivElement>(null);
  const set = (fn: (d: ChartSettings["data"]) => ChartSettings["data"], label: string) => store.getState().update((x) => ({ ...x, data: fn(x.data) }), label);
  const fieldOf = (id?: string) => ds.fields.find((x) => x.id === id);
  const drop = (slot: Slot, field: Field) => {
    if (!acceptsField(slot, field).ok) return;
    set((d) => ({ ...d, mapping: slot === "value" ? { ...d.mapping, value: { field: field.id, agg: d.mapping.value.agg === "count" ? "sum" : d.mapping.value.agg } }
      : slot === "tooltip" ? { ...d.mapping, tooltip: [...new Set([...d.mapping.tooltip, field.id])] } : { ...d.mapping, [slot]: field.id } }), `${SLOT_LABEL[slot]}: ${field.label}`);
    setHeld(null);
  };
  const clear = (slot: Slot, id?: string) => set((d) => ({ ...d, mapping: slot === "tooltip" ? { ...d.mapping, tooltip: d.mapping.tooltip.filter((t) => t !== id) }
    : slot === "value" ? { ...d.mapping, value: { agg: "count" } } : { ...d.mapping, [slot]: undefined } }), `${SLOT_LABEL[slot]} cleared`);
  const pick = (id: DatasetId) => set(() => ({ ...s.data, datasetId: id, mapping: { value: { agg: "count" }, tooltip: [] }, filters: [], followViewer: undefined }), `Dataset: ${DATASETS[id].label}`);

  return (
    <div className="cs-sections">
      {problems.length > 0 && <div className="cs-problems" role="alert">{problems.map((x) => <p key={x}>{x}</p>)}</div>}
      <Section title="Source">
        <div className="cs-source">
          <span className="cs-source__icon"><CIcon name={ds.icon} size={16} /></span>
          <span className="cs-source__text"><strong>{ds.label}</strong><small>{ds.source}</small></span>
          <button type="button" className="btn btn--secondary btn--small" onClick={() => others.current?.querySelector("button")?.focus()}><CIcon name="arrow-left-right" />Change</button>
        </div>
        <p className="cs-sub">Also in the app</p>
        <div className="cs-chips" ref={others}>
          {datasetsFor(group).filter((d) => d.id !== ds.id).map((d) => (
            <button key={d.id} type="button" className="cs-pill" disabled={d.locked} aria-label={d.locked ? `${d.label} (offices only)` : d.label} onClick={() => pick(d.id as DatasetId)}>
              <CIcon name={d.locked ? "lock" : d.icon} />{d.label}
            </button>
          ))}
        </div>
      </Section>

      <Section title="Fields">
        <div className="cs-chips" role="list">
          {ds.fields.map((fl) => (
            <span role="listitem" key={fl.id}>
              <button type="button" draggable className={`cs-pill cs-pill--field${held?.id === fl.id ? " is-held" : ""}`} aria-pressed={held?.id === fl.id}
                aria-label={`${fl.label}. ${held?.id === fl.id ? "Picked up. Press Enter on a slot to drop it." : "Press Enter to pick up."}`}
                onDragStart={(e) => { e.dataTransfer.setData("text/field", fl.id); e.dataTransfer.effectAllowed = "copy"; setHeld(fl); }} onDragEnd={() => setHeld(null)}
                onClick={() => setHeld(held?.id === fl.id ? null : fl)}
                onKeyDown={(e) => { if (e.key === "Escape" && held) { e.preventDefault(); setHeld(null); } }}>
                <CIcon name={FIELD_ICON[fl.type]} />{fl.label}
              </button>
            </span>
          ))}
        </div>
      </Section>

      <Section title="Mapping">
        <div className="cs-mapping">
          {(["category", "value", "split", "tooltip"] as Slot[]).map((slot) => {
            const verdict = held ? acceptsField(slot, held) : null;
            const current = slot === "value" ? fieldOf(s.data.mapping.value.field) : slot === "tooltip" ? undefined : fieldOf(s.data.mapping[slot]);
            const cls = verdict?.ok ? " is-target" : verdict ? " is-blocked" : "";
            return (
              <Row key={slot} label={SLOT_LABEL[slot]}>
                <div className={`cs-slot${cls}`} role="group" tabIndex={0}
                  aria-label={`${SLOT_LABEL[slot]}${current ? `: ${current.label}` : ""}${verdict ? (verdict.ok ? ". Press Enter to drop here" : `. ${verdict.reason}`) : ""}`}
                  onDragOver={(e) => { if (verdict?.ok) { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; } }}
                  onDrop={(e) => { e.preventDefault(); const fl = fieldOf(e.dataTransfer.getData("text/field")); if (fl) drop(slot, fl); }}
                  onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && held && e.target === e.currentTarget) { e.preventDefault(); drop(slot, held); } }}>
                  {verdict ? (
                    <span className="cs-slot__drop">{verdict.ok ? <><CIcon name="plus" />{slot === "split" ? `Drop to split each bar by ${held!.label}` : "Drop here"}</> : verdict.reason}</span>
                  ) : slot === "tooltip" ? (
                    <>{s.data.mapping.tooltip.map((t) => { const fl = fieldOf(t); return (
                      <span key={t} className="cs-pill cs-pill--static"><CIcon name={fl ? FIELD_ICON[fl.type] : "text"} />{fl?.label ?? t}{fl?.type === "date" ? " · latest" : ""}
                        <button type="button" className="cs-x" aria-label={`Remove ${fl?.label ?? t} from tooltip`} onClick={() => clear("tooltip", t)}><CIcon name="x" size={11} /></button></span>); })}
                      <CIcon name="plus" className="cs-slot__plus" /></>
                  ) : current ? (
                    <><span className="cs-pill cs-pill--mapped"><CIcon name={FIELD_ICON[current.type]} />{current.label}</span>
                      {slot === "value" ? null : <button type="button" className="cs-x cs-slot__clear" aria-label={`Clear ${SLOT_LABEL[slot]}`} onClick={() => clear(slot)}><CIcon name="x" size={12} /></button>}</>
                  ) : <span className="cs-slot__empty">{slot === "value" ? "Count" : ""}</span>}
                  {slot === "value" && !verdict && (
                    <Seg label="Aggregate" value={s.data.mapping.value.agg === "min" ? "max" : s.data.mapping.value.agg}
                      onChange={(agg) => set((d) => ({ ...d, mapping: { ...d.mapping, value: { ...d.mapping.value, agg } } }), `Value: ${agg}`)}
                      options={[{ id: "sum", label: "Sum" }, { id: "count", label: "Count" }, { id: "avg", label: "Avg" }, { id: "max", label: "Max" }]} />
                  )}
                </div>
              </Row>
            );
          })}
        </div>
      </Section>

      <Section title="Filters">
        <div className="cs-chips">
          {s.data.filters.map((x, i) => {
            const fl = fieldOf(x.field);
            return (
              <span key={i} className="cs-pill cs-pill--static"><CIcon name={fl ? FIELD_ICON[fl.type] : "funnel"} />
                {fl ? fl.label.charAt(0).toUpperCase() + fl.label.slice(1) : x.field} {OP_LABEL[x.op]} {prettyValue(x.value)}
                <button type="button" className="cs-x" aria-label={`Remove filter ${fl?.label ?? x.field}`} onClick={() => set((d) => ({ ...d, filters: d.filters.filter((_, j) => j !== i) }), "Filter removed")}><CIcon name="x" size={11} /></button>
              </span>
            );
          })}
          <AddFilter fields={ds.fields} onAdd={(f) => set((d) => ({ ...d, filters: [...d.filters, f] }), "Filter added")} />
        </div>
        <div className="cs-row cs-row--follow">
          <Switch on={s.data.followViewer === "ward"} label="Follow the viewer: limit to their ward"
            onChange={(on) => set((d) => ({ ...d, followViewer: on ? "ward" : undefined }), on ? "Follow viewer’s ward" : "Stop following viewer")} />
          <span className="cs-row__text">Follow the viewer: limit to their ward</span>
          <code className="token">{"{viewer.ward}"}</code>
        </div>
      </Section>

      <Section title="Updates">
        <Row label="Refresh">
          <Seg label="Refresh" value={s.data.refresh} onChange={(refresh) => set((d) => ({ ...d, refresh }), `Refresh: ${refresh}`)}
            options={[{ id: "live", label: "Live" }, { id: "hourly", label: "Hourly" }, { id: "open", label: "On open" }]} />
        </Row>
      </Section>
    </div>
  );
}

/** "+ Filter": field · operator · value, added as one history step. */
function AddFilter({ fields, onAdd }: { fields: Field[]; onAdd: (f: Filter) => void }) {
  const [open, setOpen] = useState(false);
  const [field, setField] = useState(fields[0]?.id ?? "");
  const [op, setOp] = useState<Filter["op"]>("is");
  const [value, setValue] = useState("");
  if (!open) return <button type="button" className="cs-pill" onClick={() => setOpen(true)}><CIcon name="plus" />Filter</button>;
  const submit = () => { if (value.trim()) { onAdd({ field, op, value: /^-?\d+(\.\d+)?$/.test(value) && op !== "is" ? Number(value) : value.trim() }); setValue(""); setOpen(false); } };
  return (
    <span className="cs-filter-form" onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); setOpen(false); } }}>
      <Dropdown className="cs-inline-dd" label="Filter field" size="sm" value={field} onChange={setField} options={fields.map((f) => ({ value: f.id, label: f.label }))} chevron={<CIcon name="chevron-down" size={12} />} />
      <Dropdown className="cs-inline-dd" label="Operator" size="sm" value={op} onChange={setOp} options={(Object.keys(OP_LABEL) as Filter["op"][]).map((o) => ({ value: o, label: OP_LABEL[o] }))} chevron={<CIcon name="chevron-down" size={12} />} />
      <input aria-label="Filter value" autoFocus value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") submit(); }} />
      <button type="button" className="cs-x" aria-label="Add filter" onClick={submit}><CIcon name="check" size={12} /></button>
    </span>
  );
}
